// 기간 프리셋 + searchParams 기반 기간 해석 — admin 전 페이지 공용
//
// FIKET-222: 빠른검색 프리셋은 메뉴마다 다르게 두지 않고 아래 PRESETS 하나로 통일한다.
// 페이지별 로컬 프리셋 배열을 새로 만들지 말고 SHARED_RANGE_PRESETS(_WITH_ALL)를 주입할 것.
const YMD_RE = /^\d{4}-\d{2}-\d{2}$/

/** Date → YYYY-MM-DD (로컬=KST 기준) */
export function toYmd(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export interface DateRangePreset {
  key: string
  label: string
  /** YYYY-MM-DD 범위 반환 */
  getRange: () => { from: string; to: string }
}

export type PresetKey = string

/** admin 공용 빠른검색 프리셋 — 전 페이지 동일 리스트 */
export const PRESETS: DateRangePreset[] = [
  { key: 'today', label: '오늘', getRange: () => today() },
  { key: 'yesterday', label: '어제', getRange: () => yesterday() },
  { key: '3daysAgo', label: '3일 전', getRange: () => daysAgo(3) },
  { key: '7d', label: '최근 7일', getRange: () => lastNDays(7) },
  { key: '30d', label: '최근 30일', getRange: () => lastNDays(30) },
  { key: '3m', label: '최근 3달', getRange: () => lastNMonths(3) },
  { key: '6m', label: '최근 6달', getRange: () => lastNMonths(6) },
  { key: 'thisMonth', label: '이번 달', getRange: () => thisMonth() },
]

/** '전체' — 기간 필터 해제 센티넬. from/to 를 빈 값으로 반환하므로 페이지에서 클리어 처리 */
export const ALL_PRESET_KEY = 'all'
export const ALL_PRESET: DateRangePreset = {
  key: ALL_PRESET_KEY,
  label: '전체',
  getRange: () => ({ from: '', to: '' }),
}

export interface ResolvedRange {
  from: string
  to: string
  preset: PresetKey | null
}

/**
 * from·to가 둘 다 유효하면 커스텀, 아니면 preset(기본 defaultKey)으로 계산.
 * defaultKey 는 PRESETS 의 key 중 하나여야 한다 (기본 '7d').
 */
export function resolveRange(sp: URLSearchParams, defaultKey: PresetKey = '7d'): ResolvedRange {
  const from = sp.get('from')
  const to = sp.get('to')
  if (from && to && YMD_RE.test(from) && YMD_RE.test(to)) {
    return { from, to, preset: null }
  }
  const presetParam = sp.get('preset')
  const matched = PRESETS.find((p) => p.key === presetParam)
    ?? PRESETS.find((p) => p.key === defaultKey)
    ?? PRESETS[3] // '7d'
  return { ...matched.getRange(), preset: matched.key }
}

/** 최근 N일 (오늘 포함) — N=1이면 오늘 하루 */
export function lastNDays(n: number): { from: string; to: string } {
  const today = new Date()
  const start = new Date(today)
  start.setDate(today.getDate() - (n - 1))
  return { from: toYmd(start), to: toYmd(today) }
}

/** 최근 N개월 (오늘 기준 N개월 전 ~ 오늘) */
export function lastNMonths(n: number): { from: string; to: string } {
  const today = new Date()
  const start = new Date(today)
  start.setMonth(today.getMonth() - n)
  return { from: toYmd(start), to: toYmd(today) }
}

/** 이번 달 1일 ~ 오늘 */
export function thisMonth(): { from: string; to: string } {
  const today = new Date()
  const start = new Date(today.getFullYear(), today.getMonth(), 1)
  return { from: toYmd(start), to: toYmd(today) }
}

/** 지난 달 1일 ~ 말일 */
export function lastMonth(): { from: string; to: string } {
  const today = new Date()
  const start = new Date(today.getFullYear(), today.getMonth() - 1, 1)
  const end = new Date(today.getFullYear(), today.getMonth(), 0)
  return { from: toYmd(start), to: toYmd(end) }
}

/** 오늘 하루 */
export function today(): { from: string; to: string } {
  return lastNDays(1)
}

/** N일 전 하루 (n=1이면 어제) */
export function daysAgo(n: number): { from: string; to: string } {
  const d = new Date()
  d.setDate(d.getDate() - n)
  const ymd = toYmd(d)
  return { from: ymd, to: ymd }
}

/** 어제 하루 */
export function yesterday(): { from: string; to: string } {
  return daysAgo(1)
}

/** DateRangePicker 기본/공용 프리셋 — admin 전 페이지 동일 (기간 필수 페이지용) */
export const SHARED_RANGE_PRESETS: DateRangePreset[] = PRESETS
export const DEFAULT_RANGE_PRESETS: DateRangePreset[] = PRESETS

/** '전체'(기간 해제)까지 포함한 프리셋 — 기간 없이 조회 가능한 페이지용 */
export const SHARED_RANGE_PRESETS_WITH_ALL: DateRangePreset[] = [...PRESETS, ALL_PRESET]

/** DateTimeRangePicker 프리셋 — 시:분 포함 Date 쌍 반환 */
export interface DateTimeRangePreset {
  key: string
  label: string
  getRange: () => { from: Date; to: Date }
}

/** N일 전 00:00 ~ 지금 */
export function dateTimeLastNDays(n: number): { from: Date; to: Date } {
  const to = new Date()
  const from = new Date(to)
  from.setDate(to.getDate() - n)
  from.setHours(0, 0, 0, 0)
  return { from, to }
}

/** N개월 전 00:00 ~ 지금 */
export function dateTimeLastNMonths(n: number): { from: Date; to: Date } {
  const to = new Date()
  const from = new Date(to)
  from.setMonth(to.getMonth() - n)
  from.setHours(0, 0, 0, 0)
  return { from, to }
}
